import { MAX_NAME_PART_BYTES, MODERATION_ACTIONS, MODERATION_OFFER_PERMANENT, MODERATION_OFFER_STAFF, moderationText, type LiftableAction, type ModerationAction } from "odyssey-shared";
import { PAGE_PENDING_TIMER, PENDING_SPINNER_DELAY_MS, type PendingTimer } from "../../ui/kit/pending.ts";
import { say } from "../../words.ts";
import type { ActionBody, ListScope, ListedRow, ModerationClient } from "./client.ts";
import type { ModerationAsk } from "./command.ts";
import { doneSentence, whenWords } from "./words.ts";

/**
 * The GM's side of the page (ADR-056's amendment of 2026-10-04, item 10), owned by the page rather than by React,
 * on the HUD store's pattern: the actions the gateway offered this connection, the chat commands' requests and
 * their answers as chat lines, and the Moderation window's state, its form's outcome and its list. The offer only
 * decides what the page shows: a page that was offered nothing takes no command, as a player's does not, and the
 * gateway decides every request whatever a page shows. Requests run one at a time, in the order they were asked.
 */

/**
 * What the window shows: the actions offered, whether the account may ban permanently and whether it may use the lead's staff screen, whether it is open, whether its form waits on the gateway, the
 * form's last answer (a status when it was written, an error when it was refused), the list's scope, its rows,
 * the id the next page is asked with (null when there is none) and whether the list is being read; the bans and
 * freezes that stand (the In force tab, read apart from the list) and whether they have been read; the gateway's
 * sentence when either read was refused, apart from the form's answer, or null; how many actions this desk has
 * written, which the window watches to read again what an action changed; and whether the form has waited
 * `PENDING_SPINNER_DELAY_MS` on the gateway with no answer (`slow`, which the footer draws as the kit's pending
 * spinner, P2.8-T41).
 */
export interface DeskSnapshot {
  readonly offered: readonly ModerationAction[];
  readonly permanent: boolean;
  readonly staff: boolean;
  readonly open: boolean;
  readonly busy: boolean;
  readonly status: string | null;
  readonly error: string | null;
  readonly scope: ListScope;
  readonly rows: readonly ListedRow[];
  readonly next: string | null;
  readonly loading: boolean;
  readonly standing: readonly ListedRow[];
  readonly standingRead: boolean;
  readonly listError: string | null;
  readonly written: number;
  readonly slow: boolean;
}

/**
 * The window's form as the GM filled it: `minutes` undefined for none, 0 for a permanent ban. A lift may name the
 * one action it ends (ADR-056's amendment of 2026-10-06, decision 9), never both ways: `lifts`, the id of a listed
 * row that stands, which a row's own End button gives, or `ends`, its kind. The form's own Lift names neither and
 * ends every ban and freeze that stands.
 */
export interface DeskForm {
  readonly action: ModerationAction;
  readonly familyName: string;
  readonly minutes: number | undefined;
  readonly reason: string;
  readonly note: string;
  readonly lifts?: string;
  readonly ends?: LiftableAction;
}

/**
 * What the desk runs with: the gateway's client; where a line goes in the chat log, as plain information or as
 * an error; how a moment is written (`whenWords` unless given); the page's clock; and the timer that marks a slow
 * request (`setTimeout` unless given).
 */
export interface ModerationDeskOptions {
  readonly client: ModerationClient;
  readonly say: (text: string, kind: "system" | "error") => void;
  readonly when?: (at: number) => string;
  readonly now?: () => number;
  readonly timer?: PendingTimer;
}

/**
 * A family as the form names it: the characters every name admits, at most the wire's 32.
 */
const FAMILY = new RegExp(`^[A-Za-z0-9_-]{1,${MAX_NAME_PART_BYTES}}$`);

const CLOSED: DeskSnapshot = Object.freeze({ offered: [], permanent: false, staff: false, open: false, busy: false, status: null, error: null, scope: "all", rows: [], next: null, loading: false, standing: [], standingRead: false, listError: null, written: 0, slow: false });

/**
 * The desk.
 */
export class ModerationDesk {
  private readonly listeners = new Set<() => void>();
  private readonly when: (at: number) => string;
  private readonly now: () => number;
  private snapshot: DeskSnapshot = CLOSED;
  private queue: Promise<void> = Promise.resolve();
  private slowWait: (() => void) | null = null;

  /**
   * @param options The client, the chat log's lines, the writer of moments and the clock.
   */
  public constructor(private readonly options: ModerationDeskOptions) {
    this.when = options.when ?? whenWords;
    this.now = options.now ?? Date.now;
  }

  /**
   * Hears that the snapshot changed. Works unbound, as React calls it.
   *
   * @param listener Called on each change.
   * @returns Stops hearing.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the window shows now. Works unbound, as React calls it.
   *
   * @returns The snapshot, the same object until something changes.
   */
  public readonly getSnapshot = (): DeskSnapshot => this.snapshot;

  /**
   * Takes the gateway's `moderationOffer`: the actions the account's roles allow, of which the page keeps the ones
   * this build knows, in the actions' order, and whether it carries the word that the account may ban permanently
   * (the author's ruling of 2026-10-08, "yeah i would recommend hiding it too"), and whether it carries the word that the
   * account may use the lead's staff screen (P1.9-T207).
   *
   * @param actions The words as sent.
   */
  public offer(actions: readonly string[]): void {
    this.change({ offered: MODERATION_ACTIONS.filter((action) => actions.includes(action)), permanent: actions.includes(MODERATION_OFFER_PERMANENT), staff: actions.includes(MODERATION_OFFER_STAFF) });
  }

  /**
   * The connection ended: the offer is taken back and the window closes, until the next join offers again.
   */
  public ended(): void {
    this.change({ offered: [], permanent: false, staff: false, open: false, busy: false, loading: false });
  }

  /**
   * Takes the gateway's `staffAlert` and writes it into the chat log.
   *
   * @param familyName The family the alert is about.
   * @param by The family of the GM who recorded it, empty when it has none.
   * @param text The note.
   */
  public alert(familyName: string, by: string, text: string): void {
    this.options.say(say("moderation.desk.alert", { family: familyName, by, text }), "system");
  }

  /**
   * Whether the offer is one of an account that sees players: any action beside the alert. The alert alone is a
   * Guide's, which holds `player.flag` and not `player.view` (ADR-112 decision 1.4), so its page offers `/flag` and
   * neither the window nor `/actions`; every other action's capability comes with `player.view` in every role that
   * holds it. The gateway refuses a Guide's read of the list whatever the page shows.
   *
   * @returns True when the window and the list of what stands may be offered.
   */
  private sees(): boolean {
    return this.snapshot.offered.some((action) => action !== "alert" && action !== "message");
  }

  /**
   * Takes a GM command's request from the chat input.
   *
   * @param ask The request.
   * @returns False when the gateway offered this page nothing, or for `/gm` and `/actions` the alert alone, so the
   *   chat answers as it does any word that is no command; true when the desk took it, sent or answered on the page.
   */
  public ask(ask: ModerationAsk): boolean {
    if (this.snapshot.offered.length === 0) {
      return false;
    }
    switch (ask.kind) {
      case "usage":
        this.options.say(say(`moderation.usage.${ask.command}`), "error");
        return true;
      case "window":
        if (!this.sees()) {
          return false;
        }
        this.open();
        return true;
      case "list":
        if (!this.sees()) {
          return false;
        }
        this.enqueue(async () => {
          const page = await this.options.client.list("active", null);
          if (!page.ok) {
            this.options.say(page.message, "error");
            return;
          }
          if (page.actions.length === 0) {
            this.options.say(say("moderation.desk.noneStanding"), "system");
          }
          for (const row of page.actions) {
            const end = row.until === null ? say("moderation.desk.permanent") : say("moderation.desk.until", { when: this.when(row.until) });
            this.options.say(say("moderation.desk.standing", { family: row.familyName, action: row.action, issuer: row.issuer, end, reason: row.reason }), "system");
          }
        });
        return true;
      case "act":
        if (!this.snapshot.offered.includes(ask.action)) {
          this.options.say(say("moderation.desk.notOffered"), "error");
          return true;
        }
        this.enqueue(async () => {
          const answer = await this.options.client.act({ action: ask.action, familyName: ask.familyName, minutes: ask.minutes, reason: ask.reason, note: null, ...(ask.ends === undefined ? {} : { ends: ask.ends }) });
          this.options.say(answer.ok ? doneSentence(answer, this.now(), this.when) : answer.message, answer.ok ? "system" : "error");
        });
        return true;
    }
  }

  /**
   * Opens the window and reads what stands and the list in its scope.
   */
  public readonly open = (): void => {
    if (!this.sees()) {
      return;
    }
    this.change({ open: true, status: null, error: null });
    this.enqueue(() => this.loadStanding());
    this.read(this.snapshot.scope, null);
  };

  /**
   * Forgets the form's last answer, as the window does when the GM starts another action.
   */
  public readonly clearAnswer = (): void => {
    this.change({ status: null, error: null });
  };

  /**
   * Closes the window, keeping what it read.
   */
  public readonly close = (): void => {
    this.change({ open: false });
  };

  /**
   * Records the form's action: checked on the page as the gateway checks it, sent once, and answered as the
   * window's status or its error; after an action that was written the list is read again. A press while one waits
   * does nothing. A refusal is never followed by another request of the desk's own, so a lift that named what it
   * ends and was refused ends nothing.
   *
   * @param form The form as filled.
   */
  public readonly submit = (form: DeskForm): void => {
    if (this.snapshot.busy) {
      return;
    }
    const familyName = form.familyName.trim();
    const reason = moderationText(form.reason);
    const note = form.note.trim() === "" ? null : moderationText(form.note);
    const fault = !this.snapshot.offered.includes(form.action)
      ? say("moderation.desk.notOffered")
      : !FAMILY.test(familyName)
        ? say("moderation.desk.family")
        : reason === null
          ? say("moderation.desk.reason")
          : form.note.trim() !== "" && note === null
            ? say("moderation.desk.note")
            : null;
    if (fault !== null || reason === null) {
      this.change({ status: null, error: fault });
      return;
    }
    const body: ActionBody = { action: form.action, familyName, minutes: form.minutes, reason, note, ...(form.lifts === undefined ? {} : { lifts: form.lifts }), ...(form.ends === undefined ? {} : { ends: form.ends }) };
    this.change({ busy: true, status: null, error: null });
    this.slowWait = (this.options.timer ?? PAGE_PENDING_TIMER)(() => {
      this.slowWait = null;
      this.change({ slow: true });
    }, PENDING_SPINNER_DELAY_MS);
    this.enqueue(async () => {
      const answer = await this.options.client.act(body);
      if (!answer.ok) {
        this.change({ busy: false, error: answer.message });
        return;
      }
      this.change({ busy: false, status: doneSentence(answer, this.now(), this.when), written: this.snapshot.written + 1 });
      await this.loadStanding();
      await this.load(this.snapshot.scope, null);
    });
  };

  /**
   * Shows the list in a scope, read from its newest row.
   *
   * @param scope Every action, or the bans and freezes that stand.
   */
  public readonly show = (scope: ListScope): void => {
    this.read(scope, null);
  };

  /**
   * Reads the next page of the list after its last row; with no page left, nothing.
   */
  public readonly more = (): void => {
    if (this.snapshot.next !== null) {
      this.read(this.snapshot.scope, this.snapshot.next);
    }
  };

  /**
   * Settles once every request asked for so far has been answered.
   *
   * @returns A promise that settles then.
   */
  public idle(): Promise<void> {
    return this.queue;
  }

  /**
   * Queues a read of the list.
   *
   * @param scope The scope.
   * @param before The id to read before, or null for the newest rows.
   */
  private read(scope: ListScope, before: string | null): void {
    this.change({ scope, loading: true });
    this.enqueue(() => this.load(scope, before));
  }

  /**
   * Reads a page of the list into the snapshot: the newest rows replace what was held, a later page follows it.
   *
   * @param scope The scope.
   * @param before The id to read before, or null for the newest rows.
   */
  private async load(scope: ListScope, before: string | null): Promise<void> {
    const page = await this.options.client.list(scope, before);
    if (!page.ok) {
      this.change({ loading: false, listError: page.message });
      return;
    }
    this.change({ loading: false, listError: null, scope, rows: before === null ? page.actions : [...this.snapshot.rows, ...page.actions], next: page.next });
  }

  /**
   * Reads the bans and freezes that stand, the first page of them, into the snapshot; a refusal leaves what was
   * held and shows as the form's error.
   */
  private async loadStanding(): Promise<void> {
    const page = await this.options.client.list("active", null);
    if (!page.ok) {
      this.change({ listError: page.message });
      return;
    }
    this.change({ standing: page.actions, standingRead: true, listError: null });
  }

  /**
   * Runs a request after the ones before it; a fault in one is reported to the console and the next still runs.
   *
   * @param run The request.
   */
  private enqueue(run: () => Promise<void>): void {
    this.queue = this.queue.then(run).catch((error: unknown) => {
      console.error("a moderation request failed on the page", error);
      this.change({ busy: false, loading: false });
    });
  }

  /**
   * Replaces the snapshot with some of its fields changed and tells the listeners; a change that changes nothing
   * keeps the snapshot. A change that ends the wait on the gateway cancels the slow mark's timer and clears the mark
   * with it.
   *
   * @param asked The fields to change.
   */
  private change(asked: Partial<DeskSnapshot>): void {
    const ends = asked.busy === false;
    if (ends) {
      this.slowWait?.();
      this.slowWait = null;
    }
    const fields: Partial<DeskSnapshot> = ends ? { ...asked, slow: false } : asked;
    const next = { ...this.snapshot, ...fields };
    if ((Object.keys(fields) as (keyof DeskSnapshot)[]).every((key) => next[key] === this.snapshot[key])) {
      return;
    }
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
